import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import keyBy from 'lodash.keyby';
import { AxiosError, AxiosResponse } from 'axios';
import axiosPoll from 'utils/axiosPoll';
import { postRequest, takeLatestWithCancel } from 'utils/redux-saga';
// @ts-ignore
import { enqueueSnackbar } from 'services/notifications/actions';
import { updateQueryString } from 'services/router/actions';
import { SPRAY_SUBMISSION_CONFIRMATION_MODAL } from 'scenes/Spray/scenes/Submission/components/SpraySubmissionConfirmationModal';
import {
    failedSubmitSpraySubmission,
    failedLoadSpraySubmission,
    setSpraySubmission,
    succeededSubmitSpraySubmission,
    succeededLoadSpraySubmission,
    requestLoadSpraySubmission
} from './actions';
import {
    SPRAY_SUBMISSION_SUBMIT_REQUESTED,
    SPRAY_SUBMISSION_LOAD_CANCELED,
    SPRAY_SUBMISSION_LOAD_REQUESTED
} from './constants';
import { RequestedSubmitSubmissionAction } from './types';
import { selectLoadSpraySubmissionParams } from './params';
import { formatResponseData } from './utils';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadSpraySubmissionSucceeded(
    response: AxiosResponse
): SagaIterator {
    yield all([
        put(succeededLoadSpraySubmission(response)),
        put(setSpraySubmission(keyBy(formatResponseData(response.data), 'id')))
    ]);
}

export function* loadSpraySubmissionFailed(error: AxiosError): SagaIterator {
    yield put(failedLoadSpraySubmission(error));
}

export function* loadSpraySubmission({ payload }: any): SagaIterator {
    const params = yield select(selectLoadSpraySubmissionParams, {
        spraySubmissionId: payload
    });

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/SprayExportProducts`,
            params
        },
        succeeded: loadSpraySubmissionSucceeded,
        failed: loadSpraySubmissionFailed
    });
}

export function* submitSpraySubmission(
    action: RequestedSubmitSubmissionAction
): SagaIterator {
    try {
        const id = action.payload;
        const response = yield call(postRequest, `SprayExports/${id}/resubmit`);

        if (response) {
            yield all([
                put(requestLoadSpraySubmission(id)),
                put(succeededSubmitSpraySubmission()),
                put(
                    enqueueSnackbar({
                        message: 'Submission was sent successfully',
                        options: { variant: 'success' }
                    })
                ),
                put(
                    updateQueryString({
                        [SPRAY_SUBMISSION_CONFIRMATION_MODAL]: undefined
                    })
                )
            ]);
        }
    } catch (error) {
        yield put(failedSubmitSpraySubmission(error));
        yield put(
            enqueueSnackbar({
                message: 'Submission has failed. Please, try again.',
                options: { variant: 'error' }
            })
        );
    }
}

export function* watchSpraySubmissionSubmitRequested(): SagaIterator {
    yield takeLatest(SPRAY_SUBMISSION_SUBMIT_REQUESTED, submitSpraySubmission);
}

export function* watchSpraySubmissionLoadRequested(): SagaIterator {
    yield takeLatestWithCancel(
        SPRAY_SUBMISSION_LOAD_REQUESTED,
        SPRAY_SUBMISSION_LOAD_CANCELED,
        loadSpraySubmission
    );
}

export default function* spraySubmissionsSagas(): SagaIterator {
    yield all([
        fork(watchSpraySubmissionSubmitRequested),
        fork(watchSpraySubmissionLoadRequested)
    ]);
}
